import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { LoadingCircle } from '~/components/common'
import { REGRID_TOKEN } from '~/constants'
import { cn, getParcelLabelValues } from '~/lib'
import type { Listing, Properties } from '~/models'

export default function ParcelData({
  listing,
  contentClassName,
}: {
  listing: Listing
  contentClassName?: string
}) {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const parcelQuery = useQuery({
    queryKey: ['parcel', listing.id],
    queryFn: async () => {
      const response = await fetch(
        `https://app.regrid.com/api/v2/parcels/point?lon=${listing.property.longitude}&lat=${listing.property.latitude}&token=${REGRID_TOKEN}`
      )
      const result = await response.json()
      return result
    },
  })

  const parcels: Properties[] = useMemo(() => {
    const features = parcelQuery.data?.parcels?.features || []
    return features.map((f: any) => f.properties as Properties)
  }, [parcelQuery.data])

  if (parcelQuery.isLoading) {
    return (
      <div className="flex h-32 flex-col items-center justify-center">
        <LoadingCircle className="mb-2 h-8 w-8 text-primary" />
        Loading parcel data
      </div>
    )
  } else if (parcels.length === 0) {
    return (
      <div className="flex h-32 items-center justify-center">
        No parcel data found
      </div>
    )
  }

  return (
    <div className="-mr-2">
      {parcels.length > 1 && (
        <>
          <p className="mb-2">Multiple parcels found</p>

          <div className="mb-3 flex max-w-md flex-nowrap gap-2 overflow-x-auto pb-1">
            {parcels.map((parcel, index) => {
              return (
                <button
                  className={cn(
                    'whitespace-nowrap rounded-md bg-slate-100 px-2',
                    index === selectedIndex && 'bg-primary text-white'
                  )}
                  onClick={() => setSelectedIndex(index)}>
                  {parcel.fields.address}
                </button>
              )
            })}
          </div>
        </>
      )}
      {
        <dl
          className={cn(
            'overflow-y-auto text-sm capitalize',
            contentClassName
          )}>
          {getParcelLabelValues(parcels[selectedIndex]).map(
            ({ key, label, value }) => {
              return (
                <div key={key} className="mb-1 grid grid-cols-3 gap-2">
                  <dt className="col-span-1 font-medium leading-tight">
                    {label}:
                  </dt>
                  <dd className="col-span-2 capitalize">{value}</dd>
                </div>
              )
            }
          )}
        </dl>
      }
    </div>
  )
}
